import { useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import type { paths } from '@/lib/api/types.gen';
import type { ApiErrorBody, QuoteError } from './_shared';

type SuggestBody = NonNullable<
  paths['/calc/suggest-garment']['post']['requestBody']
>['content']['application/json'];
type SuggestInput = SuggestBody['input'];

export type GarmentSuggestion = {
  style: string;
  colorName: string;
  subtitle?: string;
  perUnitSell: number;
  orderTotal: number;
  marginPct: number;
  blankCostEach: number;
  savingsPerUnit: number;
  savingsOrderTotal: number;
};

export type SuggestGarmentResult = {
  baseline: { perUnitSell: number; orderTotal: number; marginPct: number } | null;
  suggestions: GarmentSuggestion[];
  summary: string;
};

export type SuggestState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; result: SuggestGarmentResult }
  | { status: 'error'; error: QuoteError };

export function useSuggestGarment() {
  const client = useApiClient();
  const [state, setState] = useState<SuggestState>({ status: 'idle' });

  async function suggest(
    input: SuggestInput,
    colorName?: string,
    category?: 'tee' | 'hoodie',
  ): Promise<SuggestGarmentResult | null> {
    setState({ status: 'loading' });
    try {
      const { data, error, response } = await client.POST('/calc/suggest-garment', {
        body: { input, colorName, targetMarginPct: 40, limit: 3, category },
      });
      if (error || !data) {
        const envelope = error as { error?: ApiErrorBody } | undefined;
        const quoteError: QuoteError = {
          status: response.status,
          body: envelope?.error ?? {
            type: 'unknown',
            title: 'Unknown error',
            detail: `HTTP ${response.status}`,
          },
        };
        setState({ status: 'error', error: quoteError });
        return null;
      }
      setState({ status: 'success', result: data.data });
      return data.data;
    } catch (err) {
      const quoteError: QuoteError = {
        status: 0,
        body: {
          type: 'network_error',
          title: 'Could not reach the API',
          detail: err instanceof Error ? err.message : 'Request failed before reaching the server.',
        },
      };
      setState({ status: 'error', error: quoteError });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle' });
  }

  return { state, suggest, reset };
}
